/**
 * Swap `morningHeaderActivePreset` in `morning-landing.tsx` to try these looks.
 * Each preset maps to `SiteHeaderNav` bar / nav / link class names.
 * Header chrome is fixed (hero glass + light text) and does not change with theme mode.
 */
export const morningHeaderPresets = {
  /** Default: floating glass pill with blur and soft ring. */
  glass: {
    barClassName:
      'flex items-center gap-3 rounded-2xl border border-white/20 bg-white/10 px-4 py-2.5 shadow-lg ring-1 shadow-black/30 ring-white/15 backdrop-blur-2xl backdrop-saturate-150 supports-backdrop-filter:bg-white/12 sm:gap-4 sm:px-6 sm:py-3',
    navClassName: 'text-zinc-200',
    linkClassName: 'transition-colors hover:text-white',
  },
  /** Minimal underline bar — no pill, no blur. */
  minimal: {
    barClassName:
      'flex items-center gap-3 rounded-none border-0 border-b border-white/10 bg-transparent px-0 py-3 shadow-none ring-0 backdrop-blur-none sm:gap-4 sm:py-4',
    navClassName: 'text-zinc-400',
    linkClassName: 'transition-colors hover:text-white',
  },
  /** Solid dark bar for maximum contrast on busy hero imagery. */
  solid: {
    barClassName:
      'flex items-center gap-3 rounded-xl border border-white/10 bg-zinc-950/95 px-4 py-2.5 shadow-md shadow-black/40 sm:gap-4 sm:px-6 sm:py-3',
    navClassName: 'text-zinc-300',
    linkClassName: 'transition-colors hover:text-white',
  },
  /** Nav links sit in an inner pill rail inside the bar. */
  pillRail: {
    barClassName:
      'flex items-center gap-3 rounded-2xl border border-white/15 bg-zinc-950/40 px-3 py-2 shadow-lg ring-1 shadow-black/25 ring-white/10 backdrop-blur-xl sm:gap-4 sm:px-4 sm:py-2.5',
    navClassName:
      'rounded-full border border-white/10 bg-white/6 px-1.5 py-1 text-zinc-300 shadow-inner shadow-black/20',
    linkClassName:
      'rounded-full px-3 py-1.5 transition-colors hover:bg-white/10 hover:text-white',
  },
  /** Glass bar with a warm accent line along the top edge. */
  accentTop: {
    barClassName:
      'relative flex items-center gap-3 overflow-hidden rounded-none border border-white/25 bg-white/15 py-2.5 shadow-lg ring-1 shadow-black/30 ring-white/20 backdrop-blur-2xl backdrop-saturate-150 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-px before:bg-linear-to-r before:from-transparent before:via-orange-400/70 before:to-transparent supports-backdrop-filter:bg-white/18 sm:gap-4 sm:py-3',
    navClassName: 'text-zinc-200',
    linkClassName: 'transition-colors hover:text-orange-100',
  },
} as const

export type MorningHeaderPresetKey = keyof typeof morningHeaderPresets
